import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

/** Detect hub genérico (/men/, home) o SPA conocidos — aviso UI. */
export function isGenericHubUrl(url) {
  try {
    const u = new URL(String(url || "").trim());
    const host = u.hostname.replace(/^www\./, "").toLowerCase();
    if (
      /^(asos\.com|zalando\.[a-z.]+|farfetch\.com|instagram\.com)$/.test(host)
    ) {
      return true;
    }
    const parts = u.pathname.split("/").filter(Boolean);
    if (parts.length === 0) return true;
    if (
      parts.length === 1 &&
      /^(men|women|homme|femme|hombre|mujer|shop|sale)$/i.test(parts[0])
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function normalizeCustomSources(list = []) {
  return (Array.isArray(list) ? list : [])
    .map((s, i) => {
      const url = String(s?.url || "").trim();
      if (!url) return null;
      let host = "";
      try {
        host = new URL(url).hostname.replace(/^www\./, "");
      } catch {
        host = url.slice(0, 40);
      }
      return {
        id: String(s?.id || `custom-src-${i + 1}`),
        name: String(s?.name || host || `Web ${i + 1}`).slice(0, 120),
        url,
        enabled: s?.enabled !== false,
      };
    })
    .filter(Boolean);
}

/** Legacy customUrls string/array → customSources. */
export function customSourcesFromLegacy(customUrls) {
  const urls = Array.isArray(customUrls)
    ? customUrls
    : String(customUrls || "")
        .split(/[\n,]+/)
        .map((u) => u.trim())
        .filter(Boolean);
  return normalizeCustomSources(
    urls.map((url, i) => {
      let host = "";
      try {
        host = new URL(url).hostname.replace(/^www\./, "");
      } catch {
        host = url.slice(0, 40);
      }
      return {
        id: `custom-url-${i + 1}`,
        name: host || `Web ${i + 1}`,
        url,
        enabled: true,
      };
    }),
  );
}

export function customSourcesToUrls(sources = []) {
  return normalizeCustomSources(sources)
    .filter((s) => s.enabled)
    .map((s) => s.url);
}

/**
 * Lista editable de webs propias (fuentes custom del SEARCH).
 */
export default function SkillAgentCustomSourcesFields({
  sources = [],
  onChange,
}) {
  const list = useMemo(() => normalizeCustomSources(sources), [sources]);
  const [draftUrl, setDraftUrl] = useState("");
  const [draftName, setDraftName] = useState("");

  const setList = (next) => onChange?.(normalizeCustomSources(next));

  const addSource = () => {
    const url = draftUrl.trim();
    if (!url) return;
    let host = "";
    try {
      host = new URL(url).hostname.replace(/^www\./, "");
    } catch {
      /* keep */
    }
    const id = `custom-src-${Date.now()}`;
    setList([
      ...list,
      {
        id,
        name: (draftName.trim() || host || "Web").slice(0, 120),
        url,
        enabled: true,
      },
    ]);
    setDraftUrl("");
    setDraftName("");
  };

  const toggle = (id, on) => {
    setList(list.map((s) => (s.id === id ? { ...s, enabled: on } : s)));
  };

  const remove = (id) => {
    setList(list.filter((s) => s.id !== id));
  };

  const rename = (id, name) => {
    setList(list.map((s) => (s.id === id ? { ...s, name } : s)));
  };

  return (
    <Box>
      <Typography
        variant="caption"
        fontWeight={800}
        color="text.secondary"
        sx={{
          display: "block",
          mb: 0.75,
          letterSpacing: 0.04,
          textTransform: "uppercase",
        }}
      >
        Webs propias
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Cada web es distinta (Shopify, HTML, hub SPA…). Usa un
        listing/colección, no el home.
      </Typography>

      <Stack spacing={0.75} sx={{ mb: 1.25 }}>
        {list.map((s) => (
          <Stack
            key={s.id}
            direction="row"
            alignItems="flex-start"
            spacing={0.5}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              px: 1,
              py: 0.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={s.enabled !== false}
                  onChange={(e) => toggle(s.id, e.target.checked)}
                />
              }
              label=""
              sx={{ mr: 0, ml: 0 }}
            />
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                size="small"
                value={s.name}
                onChange={(e) => rename(s.id, e.target.value)}
                placeholder="Nombre"
                fullWidth
                sx={{
                  "& .MuiInputBase-input": { fontSize: "0.8rem", py: 0.5 },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ wordBreak: "break-all" }}
              >
                {s.url}
              </Typography>
              {isGenericHubUrl(s.url) && (
                <Typography variant="caption" color="warning.main">
                  SPA (ASOS/Zalando/Farfetch): mejor URL de categoría/listado.
                  Instagram: experimental (login/antibot; puede salir vacío).
                </Typography>
              )}
            </Stack>
            <IconButton
              size="small"
              aria-label="Quitar fuente"
              onClick={() => remove(s.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        {!list.length && (
          <Typography variant="caption" color="text.secondary">
            Ninguna web propia aún.
          </Typography>
        )}
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems="flex-start"
      >
        <TextField
          size="small"
          label="Nombre"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          sx={{ width: { xs: "100%", sm: 140 } }}
        />
        <TextField
          size="small"
          label="URL"
          placeholder="https://tienda.com/collections/jackets"
          value={draftUrl}
          onChange={(e) => setDraftUrl(e.target.value)}
          fullWidth
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addSource();
            }
          }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addSource}
          disabled={!draftUrl.trim()}
          sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
        >
          Añadir
        </Button>
      </Stack>
      {draftUrl.trim() && isGenericHubUrl(draftUrl) && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ mt: 0.75, display: "block" }}
        >
          SPA (ASOS/Zalando/Farfetch): URL de categoría/listado. Instagram:
          experimental (puede fallar por login/antibot).
        </Typography>
      )}
    </Box>
  );
}
